import React, { useRef, useEffect, useState, useCallback } from "react";
import "bpmn-js/dist/assets/diagram-js.css";
import "bpmn-js/dist/assets/bpmn-font/css/bpmn-embedded.css";
import "../../styles/bpmn-editor.css";
import { attachBpmnTouchNavigation } from "../../utils/bpmnTouchNavigation";

const EMPTY_BPMN = `<?xml version="1.0" encoding="UTF-8"?>
<bpmn:definitions xmlns:bpmn="http://www.omg.org/spec/BPMN/20100524/MODEL"
                  xmlns:bpmndi="http://www.omg.org/spec/BPMN/20100524/DI"
                  xmlns:dc="http://www.omg.org/spec/DD/20100524/DC"
                  xmlns:di="http://www.omg.org/spec/DD/20100524/DI"
                  id="Definitions_1"
                  targetNamespace="http://bpmn.io/schema/bpmn">
  <bpmn:process id="Process_1" isExecutable="false">
    <bpmn:startEvent id="StartEvent_1" name="Start"/>
  </bpmn:process>
  <bpmndi:BPMNDiagram id="BPMNDiagram_1">
    <bpmndi:BPMNPlane id="BPMNPlane_1" bpmnElement="Process_1">
      <bpmndi:BPMNShape id="StartEvent_1_di" bpmnElement="StartEvent_1">
        <dc:Bounds x="180" y="160" width="36" height="36"/>
      </bpmndi:BPMNShape>
    </bpmndi:BPMNPlane>
  </bpmndi:BPMNDiagram>
</bpmn:definitions>`;

const TOOLBOX_ELEMENTS = [
  { group: "Events", items: [
    { type: "startEvent", label: "Start Event", icon: "start", bpmnType: "bpmn:StartEvent", desc: "Triggers the start of a process" },
    { type: "endEvent", label: "End Event", icon: "end", bpmnType: "bpmn:EndEvent", desc: "Marks the end of a process path" },
  ]},
  { group: "Activities", items: [
    { type: "task", label: "Task", icon: "task", bpmnType: "bpmn:Task", desc: "A single unit of work" },
  ]},
  { group: "Gateways", items: [
    { type: "exclusiveGateway", label: "XOR Gateway", icon: "gateway-xor", bpmnType: "bpmn:ExclusiveGateway", desc: "Exclusive - only one path taken" },
    { type: "parallelGateway", label: "AND Gateway", icon: "gateway-and", bpmnType: "bpmn:ParallelGateway", desc: "Parallel - all paths taken" },
    { type: "inclusiveGateway", label: "OR Gateway", icon: "gateway-or", bpmnType: "bpmn:InclusiveGateway", desc: "Inclusive - one or more paths" },
  ]},
];

const TOOLBOX_GROUP_STATE = TOOLBOX_ELEMENTS.reduce((state, group) => {
  state[group.group] = false;
  return state;
}, {});

const FRIENDLY_TYPE_MAP = {
  "bpmn:Task": "Task",
  "bpmn:StartEvent": "Start Event",
  "bpmn:EndEvent": "End Event",
  "bpmn:ExclusiveGateway": "XOR Gateway",
  "bpmn:ParallelGateway": "AND Gateway",
  "bpmn:InclusiveGateway": "OR Gateway",
  "bpmn:SequenceFlow": "Sequence Flow",
  "bpmn:Process": "Process",
  "bpmn:Participant": "Participant",
  "bpmn:SubProcess": "Sub-Process",
  "bpmn:IntermediateCatchEvent": "Intermediate Catch Event",
  "bpmn:IntermediateThrowEvent": "Intermediate Throw Event",
  "bpmn:BoundaryEvent": "Boundary Event",
};

const BPMN_MARKERS = ["cs-node-task", "cs-node-start", "cs-node-end", "cs-node-gateway", "cs-flow-sequence"];

const friendlyType = (type) => {
  if (!type) return "Element";
  return FRIENDLY_TYPE_MAP[type] || type.replace("bpmn:", "");
};

const markerForType = (type) => ({
  "bpmn:Task": "cs-node-task",
  "bpmn:StartEvent": "cs-node-start",
  "bpmn:EndEvent": "cs-node-end",
  "bpmn:ExclusiveGateway": "cs-node-gateway",
  "bpmn:ParallelGateway": "cs-node-gateway",
  "bpmn:InclusiveGateway": "cs-node-gateway",
  "bpmn:SequenceFlow": "cs-flow-sequence",
}[type] || null);

const Icon = ({ children, ...props }) => <svg fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>{children}</svg>;
const SvgUndo = () => <Icon width="16" height="16" viewBox="0 0 24 24"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></Icon>;
const SvgRedo = () => <Icon width="16" height="16" viewBox="0 0 24 24"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.13-9.36L23 10"/></Icon>;
const SvgZoomIn = () => <Icon width="16" height="16" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></Icon>;
const SvgZoomOut = () => <Icon width="16" height="16" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="8" y1="11" x2="14" y2="11"/></Icon>;
const SvgFitScreen = () => <Icon width="16" height="16" viewBox="0 0 24 24"><path d="M8 3H5a2 2 0 0 0-2 2v3"/><path d="M21 8V5a2 2 0 0 0-2-2h-3"/><path d="M3 16v3a2 2 0 0 0 2 2h3"/><path d="M16 21h3a2 2 0 0 0 2-2v-3"/></Icon>;
const SvgDownload = () => <Icon width="14" height="14" viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></Icon>;
const SvgCheck = () => <Icon width="15" height="15" viewBox="0 0 24 24" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></Icon>;
const SvgSave = () => <Icon width="15" height="15" viewBox="0 0 24 24"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></Icon>;
const SvgX = () => <Icon width="15" height="15" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></Icon>;
const SvgChevronLeft = () => <Icon width="16" height="16" viewBox="0 0 24 24"><polyline points="15 18 9 12 15 6"/></Icon>;
const SvgChevronRight = () => <Icon width="16" height="16" viewBox="0 0 24 24"><polyline points="9 18 15 12 9 6"/></Icon>;
const SvgChevronDown = () => <Icon width="14" height="14" viewBox="0 0 24 24"><polyline points="6 9 12 15 18 9"/></Icon>;
const SvgMap = () => <Icon width="14" height="14" viewBox="0 0 24 24"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/><line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/></Icon>;
const SvgGrid = () => <Icon width="14" height="14" viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></Icon>;
const SvgHelp = () => <Icon width="14" height="14" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></Icon>;
const SvgPanelLeft = () => <Icon width="15" height="15" viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 3v18"/></Icon>;
const SvgPanelRight = () => <Icon width="15" height="15" viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M15 3v18"/></Icon>;
const SvgSpark = () => <Icon width="14" height="14" viewBox="0 0 24 24"><path d="m12 3 1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3Z"/></Icon>;
const SvgInfo = () => <Icon width="14" height="14" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></Icon>;
const SvgMore = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><circle cx="5" cy="12" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="19" cy="12" r="2"/></svg>;

const ToolboxGlyph = ({ kind }) => ({
  start: <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="6.5" stroke="currentColor" strokeWidth="2.2"/></svg>,
  end: <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="7.3" stroke="currentColor" strokeWidth="2.2"/><circle cx="12" cy="12" r="4.2" fill="currentColor" opacity="0.2"/></svg>,
  task: <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><rect x="5.2" y="6" width="13.6" height="12" rx="2.5" stroke="currentColor" strokeWidth="2.2"/></svg>,
  "gateway-xor": <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M12 4.2 19.8 12 12 19.8 4.2 12 12 4.2Z" stroke="currentColor" strokeWidth="2.1"/><path d="m9.2 9.2 5.6 5.6M14.8 9.2l-5.6 5.6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>,
  "gateway-and": <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M12 4.2 19.8 12 12 19.8 4.2 12 12 4.2Z" stroke="currentColor" strokeWidth="2.1"/><path d="M12 8.2v7.6M8.2 12h7.6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>,
  "gateway-or": <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M12 4.2 19.8 12 12 19.8 4.2 12 12 4.2Z" stroke="currentColor" strokeWidth="2.1"/><circle cx="12" cy="12" r="2.8" stroke="currentColor" strokeWidth="2"/></svg>,
}[kind] || null);

const ToolbarButton = ({ icon, label, title, onClick, disabled = false, pressed = false, className = "" }) => (
  <button type="button" className={`toolbar-btn${className ? ` ${className}` : ""}${pressed ? " active" : ""}`} onClick={onClick} title={title || label} aria-label={label || title} aria-pressed={typeof pressed === "boolean" ? pressed : undefined} disabled={disabled}>{icon}{label ? <span className="btn-label">{label}</span> : null}</button>
);
function BpmnEditor({
  xml,
  onXmlChange,
  onSave,
  onValidate,
  onCancel,
  readOnly = false,
  height = "calc(100vh - 180px)",
  processName = "",
  validationErrors = [],
  validationWarnings = [],
  onOpenCalledProcess,
}) {
  const containerRef = useRef(null);
  const modelerRef = useRef(null);
  const minimapRef = useRef(null);
  const overflowRef = useRef(null);
  const importedXmlRef = useRef(null);
  // Keep the latest drill-down callback so the modeler's one-time event
  // handler never closes over a stale prop.
  const onOpenCalledProcessRef = useRef(onOpenCalledProcess);
  useEffect(() => {
    onOpenCalledProcessRef.current = onOpenCalledProcess;
  }, [onOpenCalledProcess]);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [selectedElement, setSelectedElement] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [toolboxOpen, setToolboxOpen] = useState(false);
  const [propsOpen, setPropsOpen] = useState(false);
  const [showMinimap, setShowMinimap] = useState(true);
  const [showGrid, setShowGrid] = useState(true);
  const [showHelp, setShowHelp] = useState(false);
  const [showValidation, setShowValidation] = useState(true);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState(TOOLBOX_GROUP_STATE);
  const [editName, setEditName] = useState("");

  const inspectableSelection = selectedElement && selectedElement.type !== "bpmn:Process" ? selectedElement : null;

  useEffect(() => {
    setEditName(selectedElement?.businessObject?.name || "");
  }, [selectedElement]);

  useEffect(() => {
    if (!overflowOpen) return undefined;
    const handleOutsideClick = (event) => {
      if (overflowRef.current && !overflowRef.current.contains(event.target)) {
        setOverflowOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [overflowOpen]);

  const refreshMinimap = useCallback((modeler) => {
    if (!modeler || !minimapRef.current) return;
    try {
      modeler.saveSVG().then(({ svg }) => {
        if (minimapRef.current) minimapRef.current.innerHTML = svg;
      });
    } catch {}
  }, []);

  const syncElementMarker = useCallback((modeler, element) => {
    if (!modeler || !element || !element.type) return;
    const canvas = modeler.get("canvas");
    BPMN_MARKERS.forEach((marker) => canvas.removeMarker(element, marker));
    const marker = markerForType(element.type);
    if (marker) canvas.addMarker(element, marker);
  }, []);

  const applyElementMarkers = useCallback((modeler) => {
    if (!modeler) return;
    modeler.get("elementRegistry").filter((element) => Boolean(element?.type)).forEach((element) => syncElementMarker(modeler, element));
  }, [syncElementMarker]);

  useEffect(() => {
    let modeler = null;
    let detachTouch = null;
    let isDestroyed = false;
    const initModeler = async () => {
      try {
        const BpmnModeler = (await import("bpmn-js/lib/Modeler")).default;
        if (isDestroyed || !containerRef.current) return;
        modeler = new BpmnModeler({ container: containerRef.current, keyboard: { bindTo: document } });
        modelerRef.current = modeler;
        // Finger pan / pinch zoom, within the same limits as the zoom buttons.
        detachTouch = attachBpmnTouchNavigation(containerRef.current, modeler.get("canvas"), { minZoom: 0.15, maxZoom: 4 });

        modeler.on("commandStack.changed", (event) => {
          console.log("Command executed:", event.command?.constructor?.name || "unknown");
          setHasChanges(true);
          if (onXmlChange) {
            modeler.saveXML({ format: true }).then(({ xml: nextXml }) => onXmlChange(nextXml));
          }
        });

        modeler.on("connection.updateWaypoints", () => {
          setHasChanges(true);
        });

        modeler.on("selection.changed", (event) => {
          const nextSelection = event.newSelection?.[0] || null;
          setSelectedElement(nextSelection);
          if (nextSelection && nextSelection.type !== "bpmn:Process") setPropsOpen(true);
        });

        // BPMN 2.0 drill-down: double-clicking a Call Activity opens the
        // referenced child process diagram (calledElement="Process_<id>").
        modeler.on("element.dblclick", (event) => {
          const el = event.element;
          if (!el || el.type !== "bpmn:CallActivity") return;
          const called = el.businessObject?.calledElement;
          const match = /Process_(\d+)/.exec(String(called || ""));
          const handler = onOpenCalledProcessRef.current;
          if (match && handler) {
            // Stop bpmn-js from opening inline label editing on the activity.
            if (event.originalEvent?.preventDefault) {
              event.originalEvent.preventDefault();
            }
            handler(Number(match[1]), el.businessObject?.name || "");
          }
        });

        modeler.on("canvas.viewbox.changed", (event) => {
          if (event.viewbox && Number.isFinite(event.viewbox.scale) && event.viewbox.scale > 0) {
            setZoom(event.viewbox.scale);
          }
        });

        ["create.start", "shape.move.start", "connect.start", "drag.init"].forEach((name) => modeler.on(name, () => setIsDragging(true)));
        ["create.end", "shape.move.end", "connect.end", "create.cleanup", "shape.move.cleanup", "connect.cleanup", "drag.cleanup", "drag.end"].forEach((name) => modeler.on(name, () => setIsDragging(false)));
        ["shape.added", "shape.changed", "connection.added", "connection.changed"].forEach((name) => modeler.on(name, (event) => syncElementMarker(modeler, event.element)));

        const xmlToLoad = xml || EMPTY_BPMN;
        importedXmlRef.current = xmlToLoad;
        await modeler.importXML(xmlToLoad);
        applyElementMarkers(modeler);

        try {
          const canvas = modeler.get("canvas");
          canvas.zoom("fit-viewport", "auto");
          const currentZoom = canvas.zoom();
          setZoom(Number.isFinite(currentZoom) && currentZoom > 0 ? currentZoom : 1);
        } catch {
          setZoom(1);
        }

        setIsLoading(false);
        setError(null);
        refreshMinimap(modeler);
      } catch (err) {
        console.error("Failed to initialize BPMN modeler:", err);
        setError(err.message || "Failed to load BPMN editor");
        setIsLoading(false);
      }
    };
    initModeler();
    return () => {
      isDestroyed = true;
      if (detachTouch) detachTouch();
      if (modeler) modeler.destroy();
    };
  }, [applyElementMarkers, onXmlChange, refreshMinimap, syncElementMarker, xml]);

  useEffect(() => {
    // Skip re-import while the user has unsaved canvas edits — the modeler
    // already holds the latest arranged state internally. Re-importing from
    // the xml prop while edits are in flight would call importXML(), which
    // completely re-renders the diagram from scratch and discards the user's
    // manually arranged positions (BUG-04).
    if (!modelerRef.current || !xml || xml === importedXmlRef.current || hasChanges) return;
    importedXmlRef.current = xml;
    modelerRef.current.importXML(xml).then(() => {
      try {
        const canvas = modelerRef.current.get("canvas");
        canvas.zoom("fit-viewport", "auto");
        const currentZoom = canvas.zoom();
        setZoom(Number.isFinite(currentZoom) && currentZoom > 0 ? currentZoom : 1);
      } catch {
        setZoom(1);
      }
      applyElementMarkers(modelerRef.current);
      setHasChanges(false);
      refreshMinimap(modelerRef.current);
    }).catch((err) => {
      console.error("Failed to import XML:", err);
      setError(err.message);
    });
  }, [applyElementMarkers, hasChanges, refreshMinimap, xml]);

  useEffect(() => {
    if (!showMinimap || !hasChanges) return undefined;
    const timer = setTimeout(() => refreshMinimap(modelerRef.current), 800);
    return () => clearTimeout(timer);
  }, [hasChanges, refreshMinimap, showMinimap]);

  const toggleToolboxGroup = useCallback((groupName) => {
    setCollapsedGroups((previous) => ({ ...previous, [groupName]: !previous[groupName] }));
  }, []);

  const withCanvas = useCallback((fn) => {
    if (!modelerRef.current) return;
    try { fn(modelerRef.current.get("canvas")); } catch {}
  }, []);

  const handleZoomIn = useCallback(() => withCanvas((canvas) => {
    const nextZoom = Math.min((Number.isFinite(zoom) ? zoom : 1) * 1.2, 4);
    canvas.zoom(nextZoom);
    setZoom(nextZoom);
  }), [withCanvas, zoom]);

  const handleZoomOut = useCallback(() => withCanvas((canvas) => {
    const nextZoom = Math.max((Number.isFinite(zoom) ? zoom : 1) / 1.2, 0.15);
    canvas.zoom(nextZoom);
    setZoom(nextZoom);
  }), [withCanvas, zoom]);

  const handleZoomFit = useCallback(() => withCanvas((canvas) => {
    canvas.zoom("fit-viewport", "auto");
    const currentZoom = canvas.zoom();
    setZoom(Number.isFinite(currentZoom) && currentZoom > 0 ? currentZoom : 1);
  }), [withCanvas]);

  const handleZoomReset = useCallback(() => withCanvas((canvas) => {
    canvas.zoom(1);
    setZoom(1);
  }), [withCanvas]);

  const handleUndo = useCallback(() => modelerRef.current?.get("commandStack")?.undo(), []);
  const handleRedo = useCallback(() => modelerRef.current?.get("commandStack")?.redo(), []);

  const handleExportXml = useCallback(async () => {
    if (!modelerRef.current) return null;
    try {
      const { xml: nextXml } = await modelerRef.current.saveXML({ format: true });
      return nextXml;
    } catch {
      return null;
    }
  }, []);

  const handleExportSvg = useCallback(async () => {
    if (!modelerRef.current) return null;
    try {
      const { svg } = await modelerRef.current.saveSVG();
      return svg;
    } catch {
      return null;
    }
  }, []);

  const handleSave = useCallback(async () => {
    const nextXml = await handleExportXml();
    if (nextXml && onSave) {
      await onSave(nextXml);
      // The modeler already holds the just-saved, user-arranged layout, so
      // treat it as the current baseline before clearing hasChanges — otherwise
      // the parent re-passing this same XML as its `xml` prop (e.g. after a
      // refetch) would no longer match `importedXmlRef` and would re-trigger
      // the importXML effect above, discarding the arrangement (BUG-04).
      importedXmlRef.current = nextXml;
      setHasChanges(false);
    }
  }, [handleExportXml, onSave]);

  const handleValidate = useCallback(async () => {
    const nextXml = await handleExportXml();
    if (nextXml && onValidate) await onValidate(nextXml);
  }, [handleExportXml, onValidate]);

  const handleDownload = useCallback(async (format) => {
    const content = format === "svg" ? await handleExportSvg() : await handleExportXml();
    if (!content) return;
    const blob = new Blob([content], { type: format === "svg" ? "image/svg+xml" : "application/xml" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${processName || "process"}.${format === "svg" ? "svg" : "bpmn"}`;
    anchor.click();
    URL.revokeObjectURL(url);
    setOverflowOpen(false);
  }, [handleExportSvg, handleExportXml, processName]);
  const addElement = useCallback((type) => {
    if (!modelerRef.current) return;
    const bpmnType = TOOLBOX_ELEMENTS.flatMap((group) => group.items).find((item) => item.type === type)?.bpmnType;
    if (!bpmnType) return;
    try {
      const modeler = modelerRef.current;
      const selection = modeler.get("selection");
      const factory = modeler.get("elementFactory");
      const registry = modeler.get("elementRegistry");
      try {
        const autoPlace = modeler.get("autoPlace");
        let source = selection.get()[0];
        if (!source) {
          const nodes = registry.filter((element) => element.type && (element.type.includes("Task") || element.type.includes("Event") || element.type.includes("Gateway")));
          source = nodes[nodes.length - 1] || registry.get("StartEvent_1");
        }
        if (source) {
          autoPlace.append(source, factory.createShape({ type: bpmnType }));
          return;
        }
      } catch {}
      try {
        modeler.get("create").start(window.event || { clientX: 0, clientY: 0 }, factory.createShape({ type: bpmnType }));
      } catch {
        alert("Use the palette on the canvas to add elements.");
      }
    } catch {
      alert("Could not add element. Use the canvas palette.");
    }
  }, []);

  const commitRename = useCallback(() => {
    if (!modelerRef.current || !selectedElement) return;
    try {
      modelerRef.current.get("modeling").updateLabel(selectedElement, editName);
    } catch {}
  }, [editName, selectedElement]);

  if (error) {
    return (
      <div className="bpmn-editor-error">
        <div className="error-icon"><svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg></div>
        <div className="error-title">Failed to Load Editor</div>
        <div className="error-message">{error}</div>
        <button className="retry-btn" onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  const hasValidation = validationErrors.length > 0 || validationWarnings.length > 0;

  return (
    <div className="bpmn-editor-wrapper">
      <div className="bpmn-editor-toolbar">
        <div className="toolbar-section toolbar-section--left">
          <div className="toolbar-title-block">
            <span className="toolbar-eyebrow">BPMN Workspace</span>
            <span className="toolbar-title" title={processName || "BPMN Editor"}>{processName || "BPMN Editor"}</span>
          </div>
          {hasChanges && <span className="unsaved-badge">Unsaved</span>}
        </div>

        <div className="toolbar-section toolbar-section--center">
          <div className="toolbar-group-card">
            <span className="toolbar-group-title">Panels</span>
            <div className="toolbar-group">
              <ToolbarButton icon={<SvgPanelLeft />} title={toolboxOpen ? "Collapse elements panel" : "Expand elements panel"} onClick={() => setToolboxOpen((previous) => !previous)} pressed={toolboxOpen} />
              <ToolbarButton icon={<SvgPanelRight />} title={propsOpen ? "Collapse properties panel" : "Expand properties panel"} onClick={() => setPropsOpen((previous) => !previous)} pressed={propsOpen} />
            </div>
          </div>

          <div className="toolbar-group-card">
            <span className="toolbar-group-title">History</span>
            <div className="toolbar-group">
              <ToolbarButton icon={<SvgUndo />} title="Undo (Ctrl+Z)" onClick={handleUndo} />
              <ToolbarButton icon={<SvgRedo />} title="Redo (Ctrl+Y)" onClick={handleRedo} />
            </div>
          </div>

          <div className="toolbar-group-card">
            <span className="toolbar-group-title">Zoom</span>
            <div className="toolbar-group">
              <ToolbarButton icon={<SvgZoomOut />} title="Zoom out" onClick={handleZoomOut} />
              <span className="zoom-level" title="Current zoom level">{Math.round(zoom * 100)}%</span>
              <ToolbarButton icon={<SvgZoomIn />} title="Zoom in" onClick={handleZoomIn} />
              <ToolbarButton icon={<SvgFitScreen />} title="Fit diagram to screen" onClick={handleZoomFit} />
              <button type="button" className="toolbar-btn toolbar-btn--compact" onClick={handleZoomReset} title="Reset zoom to 100%" aria-label="Reset zoom">1:1</button>
            </div>
          </div>

          <div className="toolbar-group-card">
            <span className="toolbar-group-title">View</span>
            <div className="toolbar-group">
              <ToolbarButton icon={<SvgGrid />} title="Toggle background grid" onClick={() => setShowGrid((previous) => !previous)} pressed={showGrid} />
              <ToolbarButton icon={<SvgMap />} title="Toggle minimap" onClick={() => setShowMinimap((previous) => !previous)} pressed={showMinimap} />
              <ToolbarButton icon={<SvgHelp />} title="Show keyboard shortcuts and tips" onClick={() => setShowHelp((previous) => !previous)} pressed={showHelp} />
            </div>
          </div>
        </div>

        <div className="toolbar-section toolbar-section--right">
          {onValidate && <button type="button" className="toolbar-btn validate-btn" onClick={handleValidate} title="Validate diagram for errors" disabled={readOnly}><SvgCheck /><span>Validate</span></button>}
          {onSave && <button type="button" className="toolbar-btn save-btn" onClick={handleSave} disabled={!hasChanges || readOnly} title="Save diagram changes"><SvgSave /><span>Save</span></button>}
          {onCancel && <button type="button" className="toolbar-btn cancel-btn" onClick={onCancel} title="Discard changes and exit editor"><SvgX /><span>Cancel</span></button>}
          <div className="toolbar-overflow" ref={overflowRef}>
            <button type="button" className={`toolbar-btn${overflowOpen ? " active" : ""}`} onClick={() => setOverflowOpen((previous) => !previous)} title="More actions" aria-label="More actions" aria-expanded={overflowOpen}><SvgMore /></button>
            {overflowOpen && (
              <div className="toolbar-menu">
                <button type="button" className="toolbar-menu-item" onClick={() => handleDownload("xml")} title="Download BPMN XML"><SvgDownload /><span>Export BPMN</span></button>
                <button type="button" className="toolbar-menu-item" onClick={() => handleDownload("svg")} title="Download SVG"><SvgDownload /><span>Export SVG</span></button>
              </div>
            )}
          </div>
        </div>
      </div>

      {hasValidation && showValidation && (
        <div className="bpmn-validation-panel">
          <div className="validation-panel-header">
            <span className="validation-panel-title">
              {validationErrors.length > 0 && <span className="val-count error">{validationErrors.length} error{validationErrors.length > 1 ? "s" : ""}</span>}
              {validationWarnings.length > 0 && <span className="val-count warning">{validationWarnings.length} warning{validationWarnings.length > 1 ? "s" : ""}</span>}
            </span>
            <button type="button" className="validation-close" onClick={() => setShowValidation(false)} title="Dismiss validation summary">x</button>
          </div>
          <div className="validation-list">
            {validationErrors.map((item, index) => <div key={`error-${index}`} className="validation-message error"><span className="validation-icon">x</span><span>{item.message || item}</span></div>)}
            {validationWarnings.map((item, index) => <div key={`warning-${index}`} className="validation-message warning"><span className="validation-icon">!</span><span>{item.message || item}</span></div>)}
          </div>
        </div>
      )}

      <div className="bpmn-editor-body" style={{ height }}>
        <aside className={`bpmn-toolbox${toolboxOpen ? " open" : " collapsed"}`}>
          <button type="button" className="panel-rail-toggle toolbox-toggle" onClick={() => setToolboxOpen((previous) => !previous)} title={toolboxOpen ? "Collapse elements panel" : "Expand elements panel"}>
            {toolboxOpen ? <SvgChevronLeft /> : <SvgChevronRight />}<span>{toolboxOpen ? "Elements" : "Open"}</span>
          </button>
          {toolboxOpen && (
            <div className="panel-content toolbox-content">
              <div className="panel-header">
                <span className="panel-kicker">Elements</span>
                <h3>Add BPMN blocks</h3>
                <p>Choose a category, then click an item to place the next step in the process.</p>
              </div>
              <div className="toolbox-scroll">
                {TOOLBOX_ELEMENTS.map((group) => {
                  const collapsed = collapsedGroups[group.group];
                  return (
                    <section key={group.group} className={`toolbox-group${collapsed ? " collapsed" : ""}`}>
                      <button type="button" className="toolbox-group-trigger" onClick={() => toggleToolboxGroup(group.group)} title={`${collapsed ? "Expand" : "Collapse"} ${group.group}`} aria-expanded={!collapsed}>
                        <div className="toolbox-group-heading">
                          <span className="toolbox-group-label">{group.group}</span>
                          <span className="toolbox-group-count">{group.items.length} item{group.items.length > 1 ? "s" : ""}</span>
                        </div>
                        <span className="toolbox-group-chevron"><SvgChevronDown /></span>
                      </button>
                      {!collapsed && <div className="toolbox-group-items">{group.items.map((item) => (
                        <button key={item.type} type="button" className="toolbox-item" onClick={() => addElement(item.type)} title={item.desc} disabled={readOnly}>
                          <span className="toolbox-item-icon"><ToolboxGlyph kind={item.icon} /></span>
                          <span className="toolbox-item-copy"><span className="toolbox-item-label">{item.label}</span><span className="toolbox-item-desc">{item.desc}</span></span>
                        </button>
                      ))}</div>}
                    </section>
                  );
                })}
              </div>
            </div>
          )}
        </aside>

        <section className="bpmn-workspace">
          <div className="workspace-shell">
            <div className="workspace-header">
              <div>
                <span className="panel-kicker">Canvas</span>
                <h3>Diagram workspace</h3>
                <p>Focus on the flow, select any node to inspect it, and use the side panels only when you need them.</p>
              </div>
              <div className="workspace-status">
                <span className={`workspace-chip${isDragging ? " active" : ""}`}>{isDragging ? "Placing element" : "Ready to edit"}</span>
                <span className="workspace-chip workspace-chip--muted">{inspectableSelection ? friendlyType(inspectableSelection.type) : "No selection"}</span>
              </div>
            </div>
            <div className={`bpmn-editor-container${showGrid ? " with-grid" : ""}${isDragging ? " dragging" : ""}`}>
              {isLoading && <div className="bpmn-editor-loading"><div className="loading-spinner" /><span>Initializing BPMN Editor...</span></div>}
              {isDragging && !isLoading && <div className="canvas-drop-indicator"><SvgSpark /><span>Drop to place, connect, or realign the flow</span></div>}
              <div ref={containerRef} className={`bpmn-canvas${isLoading ? " hidden" : ""}`} style={{ width: "100%", height: "100%", touchAction: "none" }} />
              {showMinimap && !isLoading && <div className="bpmn-minimap" title="Diagram overview"><div className="minimap-label">Overview</div><div className="minimap-content" ref={minimapRef} /></div>}
              {showHelp && (
                <div className="bpmn-help-fixed">
                  <div className="help-fixed-header"><span>Keyboard Shortcuts</span><button type="button" onClick={() => setShowHelp(false)} title="Close help">x</button></div>
                  <ul className="help-fixed-list">
                    <li><kbd>Ctrl+Z</kbd><span>Undo the last action</span></li>
                    <li><kbd>Ctrl+Y</kbd><span>Redo the previous action</span></li>
                    <li><kbd>Del</kbd><span>Delete the current selection</span></li>
                    <li><kbd>Scroll</kbd><span>Zoom the diagram in or out</span></li>
                    <li><kbd>Drag</kbd><span>Move nodes, reconnect lines, or pan the canvas</span></li>
                    <li><kbd>Double</kbd><span>Edit a label directly on the canvas</span></li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </section>

        <aside className={`bpmn-props-panel${propsOpen ? " open" : " collapsed"}`}>
          <button type="button" className="panel-rail-toggle props-toggle" onClick={() => setPropsOpen((previous) => !previous)} title={propsOpen ? "Collapse properties panel" : "Expand properties panel"}>
            {propsOpen ? <SvgChevronRight /> : <SvgChevronLeft />}<span>{propsOpen ? "Properties" : "Open"}</span>
          </button>
          {propsOpen && (
            <div className="panel-content props-content">
              <div className="panel-header">
                <span className="panel-kicker">Inspector</span>
                <h3>Element properties</h3>
                <p>View and update details for the selected BPMN element without leaving the canvas.</p>
              </div>
              {inspectableSelection ? (
                <div className="props-sections">
                  <section className="prop-section-card">
                    <div className="prop-section-heading"><span>General</span></div>
                    <div className="prop-summary"><span className="prop-type-badge">{friendlyType(inspectableSelection.type)}</span><span className="prop-id">{inspectableSelection.id}</span></div>
                    <div className="prop-field">
                      <label className="prop-label" htmlFor="bpmn-prop-name">Name</label>
                      <input id="bpmn-prop-name" className="prop-input" value={editName} onChange={(event) => setEditName(event.target.value)} onBlur={commitRename} onKeyDown={(event) => { if (event.key === "Enter") commitRename(); }} placeholder="Enter element name..." disabled={readOnly} />
                    </div>
                  </section>

                  <section className="prop-section-card">
                    <div className="prop-section-heading"><span>Advanced</span></div>
                    <div className="prop-field"><span className="prop-label">Element ID</span><div className="prop-static-value">{inspectableSelection.id}</div></div>
                    {inspectableSelection.businessObject?.documentation?.[0]?.text ? (
                      <div className="prop-field"><span className="prop-label">Documentation</span><p className="prop-documentation">{inspectableSelection.businessObject.documentation[0].text}</p></div>
                    ) : (
                      <div className="prop-inline-hint"><SvgInfo /><span>No advanced documentation is available for this element.</span></div>
                    )}
                  </section>

                  <section className="prop-section-card">
                    <div className="prop-section-heading"><span>Conditions</span></div>
                    {inspectableSelection.type === "bpmn:SequenceFlow" ? (
                      <>
                        <div className="prop-field"><span className="prop-label">From</span><div className="prop-static-value">{inspectableSelection.source?.businessObject?.name || inspectableSelection.source?.id || "-"}</div></div>
                        <div className="prop-field"><span className="prop-label">To</span><div className="prop-static-value">{inspectableSelection.target?.businessObject?.name || inspectableSelection.target?.id || "-"}</div></div>
                        <div className="prop-field"><span className="prop-label">Bendpoints</span><div className="prop-static-value">{inspectableSelection.waypoints ? inspectableSelection.waypoints.length - 2 : 0} bendpoint(s)</div></div>
                        <p className="prop-hint">Drag the line or its handles to refine the route between connected elements.</p>
                      </>
                    ) : (
                      <div className="prop-inline-hint"><SvgInfo /><span>Condition details appear here when a sequence flow is selected.</span></div>
                    )}
                  </section>
                </div>
              ) : (
                <div className="props-empty">
                  <div className="props-empty-illustration"><svg width="56" height="56" viewBox="0 0 56 56" fill="none"><rect x="8" y="10" width="40" height="36" rx="10" fill="#eff6ff"/><circle cx="19" cy="19" r="5" stroke="#3b82f6" strokeWidth="2"/><rect x="27" y="16" width="14" height="7" rx="3.5" fill="#bfdbfe"/><rect x="16" y="30" width="24" height="7" rx="3.5" fill="#dbeafe"/></svg></div>
                  <strong>Select an element</strong>
                  <span>Click any task, event, gateway, or sequence flow on the canvas to inspect and edit its properties here.</span>
                </div>
              )}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

export default BpmnEditor;
